import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { User, Broadcast, Users as UsersIcon } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'Women', label: 'Women' },
  { id: 'Men', label: 'Men' },
  { id: 'Couples', label: 'Couples' },
  { id: 'Trans', label: 'Trans' },
];

const StreamThumbnail = ({ stream }) => (
  <Link
    to={`/live?watch=${stream.id}`}
    className="group block rounded-xl overflow-hidden bg-slate-900 border border-white/5 hover:border-blue-500/30 transition-all hover:shadow-lg hover:shadow-blue-500/5"
    data-testid={`stream-thumbnail-${stream.id}`}
  >
    <div className="aspect-video relative bg-slate-800">
      {stream.thumbnail_url ? (
        <img src={stream.thumbnail_url} alt={stream.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-900/30 to-slate-900">
          <Broadcast weight="duotone" className="w-12 h-12 text-blue-500/40" />
        </div>
      )}
      {/* Live badge */}
      <div className="absolute top-2 left-2 px-2 py-0.5 bg-red-600 text-white text-xs font-bold rounded flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
        LIVE
      </div>
      {/* Viewer count */}
      <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/70 text-slate-300 text-xs rounded flex items-center gap-1">
        <UsersIcon className="w-3 h-3" />
        {stream.viewer_count || 0}
      </div>
    </div>
    <div className="p-3">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-7 h-7 rounded-full bg-slate-800 flex items-center justify-center overflow-hidden shrink-0">
          {stream.avatar_url ? (
            <img src={stream.avatar_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <User className="w-4 h-4 text-slate-500" />
          )}
        </div>
        <span className="text-white font-medium text-sm truncate">{stream.streamer_name}</span>
      </div>
      {stream.title && (
        <p className="text-slate-500 text-xs truncate">{stream.title}</p>
      )}
    </div>
  </Link>
);

const ExplorePage = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStreams = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/streaming/live`);
      setStreams(res.data || []);
    } catch (err) {
      console.error('Failed to fetch streams');
      setStreams([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStreams();
    // Refresh every 30 seconds
    const interval = setInterval(fetchStreams, 30000);
    return () => clearInterval(interval);
  }, [fetchStreams]);

  const filteredStreams = streams.filter(s => {
    if (activeTab === 'all') return true;
    return s.category?.toLowerCase() === activeTab.toLowerCase();
  });

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="explore-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-white mb-1">Live Now</h1>
        <p className="text-slate-500 text-sm">Watch live broadcasts from creators</p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-6 border-b border-white/5 pb-4" data-testid="explore-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-2.5 rounded-full font-medium text-sm transition-colors ${
              activeTab === tab.id
                ? 'bg-blue-500 text-white'
                : 'bg-slate-900 text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredStreams.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-20 h-20 rounded-full bg-slate-900 flex items-center justify-center mx-auto mb-4">
            <Broadcast className="w-10 h-10 text-slate-600" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">
            {activeTab === 'all' ? 'No one is live right now' : `No ${activeTab.toLowerCase()} streaming right now`}
          </h2>
          <p className="text-slate-500">Check back soon or go live yourself!</p>
          <Link
            to="/live"
            className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600 transition-colors"
            data-testid="go-live-cta"
          >
            <Broadcast weight="fill" className="w-4 h-4" />
            Go Live
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filteredStreams.map(stream => (
            <StreamThumbnail key={stream.id} stream={stream} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ExplorePage;
